import { useEffect } from "react";
import { X, Download } from "lucide-react";

/** Full-screen image preview with download.
 *  `img` shape: { url, filename, mime } — url is the (already authed) blob or
 *  authed URL used for the thumbnail so no re-fetching is required to view.
 *  Escape / click backdrop / X button all close.
 */
export default function ImageLightbox({ img, onClose }) {
  useEffect(() => {
    if (!img) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [img, onClose]);

  if (!img) return null;

  const download = async () => {
    try {
      // The url passed in is already authed (has ?token=…), so a bare fetch works.
      const res = await fetch(img.url, { credentials: "omit" });
      if (!res.ok) throw new Error("Fetch failed");
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = img.filename || "image";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="image-lightbox"
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute top-3 right-3 flex items-center gap-2">
        <button
          data-testid="lightbox-download"
          onClick={(e) => { e.stopPropagation(); download(); }}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
          title="Download"
        >
          <Download className="w-5 h-5" />
        </button>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <img
        src={img.url}
        alt={img.filename || "image"}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
      />
      {img.filename && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 text-white text-xs backdrop-blur">
          {img.filename}
        </div>
      )}
    </div>
  );
}
